import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  TextField,
  Typography,
  Checkbox,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import HowToRegOutlinedIcon from "@material-ui/icons/HowToRegOutlined";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import {
  BsBriefcase,
  BsCheck2Circle,
  BsClipboardData,
  BsClockHistory,
  BsEnvelope,
  BsFileEarmarkText,
  BsInfoCircle,
  BsPeople,
} from "react-icons/bs";
import { shallowEqual, useSelector } from "react-redux";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
  getCongresoEdicionById,
  getCongresoContactosByEdicion,
  getCongresoInscripcionesByEdicion,
  getCongresoInscripcionById,
  getCongresoTalleresInscripciones,
  postCongresoTallerInscripcion,
  deleteCongresoTallerInscripcion,
  deleteCongresoInscripcionesBulk,
  deleteCongresoTalleresInscripcionesBulk,
} from "../../../../api/congresoEdicion";
import { SERVER_URL } from "../../../../api";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { exportCongresoInscripcionesExcel } from "../../../../utils/exportCongresoInscripcionesExcel";
import { exportCongresoContactosExcel } from "../../../../utils/exportCongresoContactosExcel";
import { exportCongresoEdicionTodoExcel } from "../../../../utils/exportCongresoEdicionTodoExcel";
import {
  exportCongresoTalleresInscripcionesExcel,
  exportCongresoTallerInscripcionesExcel,
} from "../../../../utils/exportCongresoTalleresInscripcionesExcel";
import { useSkeleton } from "../../../hooks/useSkeleton";
import "./verCongresoEdicionPage.scss";
import "./congresoEdicion.scss";

const VIEW_TAB_TODO = "todo";
const VIEW_TAB_TALLERES = "talleres";
const VIEW_TAB_INSCRIPCIONES = "inscripciones";
const VIEW_TAB_PDFS = "pdfs";
const VIEW_TAB_ENVIOS = "envios";
const VIEW_TAB_PATROCINIOS = "patrocinios";
const VIEW_TAB_CONTACTO = "contacto";

const VIEW_APARTADOS = [
  { id: VIEW_TAB_TODO, label: "Todo", Icon: DashboardOutlinedIcon },
  { id: VIEW_TAB_TALLERES, label: "Talleres", Icon: SchoolOutlinedIcon },
  { id: VIEW_TAB_INSCRIPCIONES, label: "Inscripciones", Icon: HowToRegOutlinedIcon },
  { id: VIEW_TAB_PDFS, label: "PDFs", Icon: PictureAsPdfOutlinedIcon },
  { id: VIEW_TAB_ENVIOS, label: "Envíos de Trabajo", Icon: DescriptionOutlinedIcon },
  { id: VIEW_TAB_PATROCINIOS, label: "Patrocinios", Icon: BusinessOutlinedIcon },
  { id: VIEW_TAB_CONTACTO, label: "Contacto Congreso", Icon: MailOutlineIcon },
];

function verCongresoLastVisitKey(edicionId, tab) {
  return `boVerCongreso:lastVisit:${edicionId}:${tab}`;
}

function readVerCongresoLastVisit(edicionId, tab) {
  if (!edicionId) return null;
  try {
    const raw = localStorage.getItem(verCongresoLastVisitKey(edicionId, tab));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeVerCongresoLastVisit(edicionId, tab) {
  if (!edicionId) return;
  try {
    localStorage.setItem(verCongresoLastVisitKey(edicionId, tab), String(Date.now()));
  } catch {
    /* ignore */
  }
}

function isContactoPendiente(row) {
  return String(row?.gestionado || "").trim().toLowerCase() !== "gestionado";
}

function countCreatedSince(rows, sinceTs) {
  if (sinceTs == null || !Number.isFinite(sinceTs)) return 0;
  let n = 0;
  (rows || []).forEach((row) => {
    if (!row?.createdAt) return;
    const t = new Date(row.createdAt).getTime();
    if (Number.isFinite(t) && t > sinceTs) n += 1;
  });
  return n;
}function validHex6Optional(value) {
  if (value && typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value.trim())) {
    return value.trim();
  }
  return null;
}

function sanitizeNavBackground(raw) {
  const s = raw && String(raw).trim();
  if (!s) return null;
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  if (/^rgba?\(/i.test(s) && s.length <= 100 && !/[<>;`\\]/.test(s)) return s;
  const lower = s.toLowerCase();
  const isGradient =
    lower.startsWith("linear-gradient(") ||
    lower.startsWith("radial-gradient(") ||
    lower.startsWith("conic-gradient(") ||
    lower.startsWith("repeating-linear-gradient(") ||
    lower.startsWith("repeating-radial-gradient(");
  if (
    isGradient &&
    s.length <= 600 &&
    !/[<>;`\\]/.test(s) &&
    !/url\s*\(/i.test(s)
  ) {
    return s;
  }
  return null;
}

function buildEditionThemeStyle(edition) {
  const navBg = sanitizeNavBackground(edition?.themeNavBackground);
  const navAccent = validHex6Optional(edition?.themeNavAccent);
  const accentSecondary = validHex6Optional(edition?.themeAccentSecondary);
  return {
    "--edition-nav-bg": navBg || "#00033c",
    ...(navAccent ? { "--edition-nav-accent": navAccent } : {}),
    ...(accentSecondary ? { "--edition-accent-secondary": accentSecondary } : {}),
  };
}

function getLocalizedText(obj, lang = "es") {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.es || obj.ca || obj.en || "";
}

function mediaUrl(path) {
  if (!path) return "";
  const p = String(path).trim().replace(/^\//, "");
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${SERVER_URL}/${p}`;
}

function groupInscripcionesByPlan(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = row.planId || "sin-plan";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  const arr = Array.from(map.entries()).map(([planId, rws]) => ({
    planId,
    rows: rws,
    maxPrice: Math.max(0, ...rws.map((r) => Number(r.precioImporte) || 0)),
    label: rws[0]?.planTituloSnapshot || planId,
  }));
  arr.sort((a, b) => b.maxPrice - a.maxPrice);
  return arr;
}

const INSCRIPCION_ESTADO_META = {
  completado: { label: "Finalizada", pill: "ok" },
  pendiente_pago: { label: "Pendiente de pago", pill: "pending" },
  fallido: { label: "Pago fallido", pill: "bad" },
  cancelado: { label: "Cancelada", pill: "bad" },
};

const PERFIL_INSCRIPCION_LABEL = {
  no_socio: "No socio",
  estudiante_no_socio: "Estudiante no socio",
  socio_actedi: "Socio ACTEDI",
  estudiante: "Estudiante",
};

const INSCRIPCION_FORM_FIELDS = [
  { key: "nombre", label: "Nombre" },
  { key: "apellido1", label: "Primer apellido" },
  { key: "apellido2", label: "Segundo apellido" },
  { key: "dni", label: "DNI / NIE" },
  { key: "fechaNacimiento", label: "Fecha de nacimiento" },
  { key: "genero", label: "Género" },
  { key: "direccion", label: "Dirección" },
  { key: "codigoPostal", label: "Código postal" },
  { key: "poblacion", label: "Población" },
  { key: "telefono", label: "Teléfono" },
  { key: "email", label: "Correo electrónico" },
  { key: "titulacion", label: "Titulación" },
  { key: "situacionLaboral", label: "Situación laboral" },
  { key: "centroTrabajo", label: "Centro de trabajo" },
  { key: "archivoTemporal", label: "Matrícula / documento adjunto" },
  { key: "aceptaPolitica", label: "Acepta política y condiciones" },
];

function formatInscripcionEur(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return `${value ?? "—"} €`;
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n} €`;
  }
}

function inscripcionEstadoLabel(estado) {
  return INSCRIPCION_ESTADO_META[estado]?.label || estado || "—";
}

function inscripcionEstadoPillClass(estado) {
  const pill = INSCRIPCION_ESTADO_META[estado]?.pill;
  if (pill === "ok") return "bo-ver-congreso__estado bo-ver-congreso__estado--ok";
  if (pill === "pending") return "bo-ver-congreso__estado bo-ver-congreso__estado--pending";
  if (pill === "bad") return "bo-ver-congreso__estado bo-ver-congreso__estado--bad";
  return "bo-ver-congreso__estado bo-ver-congreso__estado--neutral";
}

function formatDetailDate(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
}

function compareTallerInscritos(a, b, key, dir) {
  const mul = dir === "asc" ? 1 : -1;
  const get = (row) => {
    if (key === "createdAt") {
      const t = row?.createdAt ? new Date(row.createdAt).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    }
    if (key === "congreso") {
      return row?.inscripcionCongresoId ? 1 : 0;
    }
    return String(row?.[key] || "").trim().toLowerCase();
  };
  const va = get(a);
  const vb = get(b);
  if (typeof va === "number" && typeof vb === "number") {
    if (va === vb) return 0;
    return va < vb ? -1 * mul : 1 * mul;
  }
  return String(va).localeCompare(String(vb), "es", { sensitivity: "base" }) * mul;
}

function sortTallerInscritos(rows, sort) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const key = sort?.key || "createdAt";
  const dir = sort?.dir === "asc" ? "asc" : "desc";
  list.sort((a, b) => compareTallerInscritos(a, b, key, dir));
  return list;
}

const TALLER_SORT_COLUMNS = [
  { key: "nombre", label: "Nombre" },
  { key: "email", label: "Email" },
  { key: "dni", label: "DNI" },
  { key: "createdAt", label: "Fecha inscripción" },
  { key: "congreso", label: "Congreso" },
];

/** Ruta guardada en formData.archivoTemporal: relativa, absoluta con /uploads/, o URL completa. */
function inscripcionAdjuntoPublicUrl(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim().replace(/\\/g, "/");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const lower = s.toLowerCase();
  const idx = lower.indexOf("uploads/");
  const rel = idx >= 0 ? s.slice(idx) : s.replace(/^\//, "");
  return mediaUrl(rel);
}

function formatFormFieldValue(key, raw) {
  if (raw === undefined || raw === null || raw === "") return "—";
  if (key === "aceptaPolitica") return raw === true ? "Sí" : raw === false ? "No" : String(raw);
  if (key === "archivoTemporal" && typeof raw === "string") {
    const href = inscripcionAdjuntoPublicUrl(raw);
    if (!href) return "—";
    const tail = String(raw).replace(/\\/g, "/").split("/").filter(Boolean).pop() || "adjunto";
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        Abrir archivo ({tail})
      </a>
    );
  }
  return String(raw);
}

function participantDisplayName(detail) {
  const fd = detail && detail.formData && typeof detail.formData === "object" ? detail.formData : {};
  const s = [fd.nombre, fd.apellido1, fd.apellido2].filter(Boolean).join(" ").trim();
  return s || "—";
}

function InscripcionDetailContent({ detail }) {
  const fd = detail && detail.formData && typeof detail.formData === "object" ? detail.formData : {};
  const usedKeys = new Set(INSCRIPCION_FORM_FIELDS.map((f) => f.key));
  const extraEntries = Object.entries(fd).filter(([k]) => !usedKeys.has(k));

  const metaCards = [
    { label: "Estado", value: inscripcionEstadoLabel(detail.estado) },
    { label: "Plan", value: detail.planTituloSnapshot || detail.planId || "—" },
    { label: "Importe", value: formatInscripcionEur(detail.precioImporte) },
    {
      label: "Perfil",
      value: detail.perfilInscripcion
        ? PERFIL_INSCRIPCION_LABEL[detail.perfilInscripcion] || detail.perfilInscripcion
        : "—",
    },
    { label: "Email contacto", value: detail.emailContacto || "—" },
    { label: "Alta", value: formatDetailDate(detail.createdAt) },
    { label: "Última actualización", value: formatDetailDate(detail.updatedAt) },
    { label: "Pedido Redsys", value: detail.redsysOrder || "—" },
    { label: "Mensaje error", value: detail.errorMessage || "—" },
    {
      label: "Talleres elegidos",
      value: Array.isArray(detail.tallerIdsSeleccionados) && detail.tallerIdsSeleccionados.length
        ? `${detail.tallerIdsSeleccionados.length} (se confirman al pagar)`
        : "Ninguno",
    },
  ];

  return (
    <>
      <div className="bo-ver-congreso-insc-dialog__pay-strip">
        <div className="bo-ver-congreso-insc-dialog__pay-main">
          <p className="bo-ver-congreso-insc-dialog__pay-kicker">Resumen</p>
          <div className="bo-ver-congreso-insc-dialog__pay-identity">
            <p className="bo-ver-congreso-insc-dialog__pay-plan-label">Plan</p>
            <p className="bo-ver-congreso-insc-dialog__pay-plan">{detail.planTituloSnapshot || detail.planId || "—"}</p>
            <p className="bo-ver-congreso-insc-dialog__pay-person-label">Participante</p>
            <p className="bo-ver-congreso-insc-dialog__pay-person">{participantDisplayName(detail)}</p>
          </div>
          <span
            className={`${inscripcionEstadoPillClass(detail.estado)} bo-ver-congreso-insc-dialog__pill-lg`}
          >
            {inscripcionEstadoLabel(detail.estado)}
          </span>
        </div>
        <div className="bo-ver-congreso-insc-dialog__pay-side" aria-label="Importe">
          <span className="bo-ver-congreso-insc-dialog__pay-price">{formatInscripcionEur(detail.precioImporte)}</span>
        </div>
      </div>

      <ExpansionPanel
        className="bo-ver-congreso-insc-dialog__expand"
        defaultExpanded={false}
        elevation={0}
      >
        <ExpansionPanelSummary
          className="bo-ver-congreso-insc-dialog__expand-summary"
          expandIcon={<ExpandMoreIcon className="bo-ver-congreso-insc-dialog__expand-icon" />}
        >
          <div className="bo-ver-congreso-insc-dialog__section-head bo-ver-congreso-insc-dialog__section-head--inline">
            <div className="bo-ver-congreso-insc-dialog__icon-ring" aria-hidden>
              <BsClipboardData size={22} />
            </div>
            <span className="bo-ver-congreso-insc-dialog__section-title">Datos administrativos</span>
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className="bo-ver-congreso-insc-dialog__expand-details">
          <div className="bo-ver-congreso-insc-dialog__meta-grid">
            {metaCards.map((m) => (
              <div key={m.label} className="bo-ver-congreso-insc-dialog__meta-card">
                <div className="bo-ver-congreso-insc-dialog__meta-card-label">{m.label}</div>
                <div className="bo-ver-congreso-insc-dialog__meta-card-value">{m.value}</div>
              </div>
            ))}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel
        className="bo-ver-congreso-insc-dialog__expand"
        defaultExpanded
        elevation={0}
      >
        <ExpansionPanelSummary
          className="bo-ver-congreso-insc-dialog__expand-summary"
          expandIcon={<ExpandMoreIcon className="bo-ver-congreso-insc-dialog__expand-icon" />}
        >
          <div className="bo-ver-congreso-insc-dialog__section-head bo-ver-congreso-insc-dialog__section-head--inline">
            <div className="bo-ver-congreso-insc-dialog__icon-ring" aria-hidden>
              <BsFileEarmarkText size={22} />
            </div>
            <span className="bo-ver-congreso-insc-dialog__section-title">Datos del formulario</span>
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className="bo-ver-congreso-insc-dialog__expand-details">
          <div className="bo-ver-congreso-insc-dialog__grid">
            {INSCRIPCION_FORM_FIELDS.map(({ key, label }) => (
              <div key={key} className="bo-ver-congreso-insc-dialog__field">
                <div className="bo-ver-congreso-insc-dialog__field-label">{label}</div>
                <div className="bo-ver-congreso-insc-dialog__field-value">{formatFormFieldValue(key, fd[key])}</div>
              </div>
            ))}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>

      {extraEntries.length > 0 ? (
        <div className="bo-ver-congreso-insc-dialog__panel">
          <div className="bo-ver-congreso-insc-dialog__section-head">
            <div className="bo-ver-congreso-insc-dialog__icon-ring" aria-hidden>
              <BsInfoCircle size={22} />
            </div>
          </div>
          <hr className="bo-ver-congreso-insc-dialog__rule" />
          <div className="bo-ver-congreso-insc-dialog__grid">
            {extraEntries.map(([key, val]) => (
              <div key={key} className="bo-ver-congreso-insc-dialog__field">
                <div className="bo-ver-congreso-insc-dialog__field-label">{key}</div>
                <div className="bo-ver-congreso-insc-dialog__field-value">
                  {val !== null && typeof val === "object" ? JSON.stringify(val) : formatFormFieldValue(key, val)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

const CONTACTO_SECCIONES = [
  {
    key: "patrocinio",
    origen: "web_congresos_patrocinio",
    title: "Patrocinios",
    Icon: BsBriefcase,
  },
  {
    key: "envio",
    origen: "web_congresos_envio_trabajo",
    title: "Envíos de trabajo",
    Icon: BsFileEarmarkText,
  },
  {
    key: "contacto",
    origen: "web_congresos",
    title: "Contacto congreso",
    Icon: BsEnvelope,
  },
];

function resolveDocumentoPdfUrl(documentoPdf) {
  const p = String(documentoPdf || "").trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return mediaUrl(p);
}

function truncateText(value, max = 80) {
  const s = value != null ? String(value).trim() : "";
  if (!s) return "—";
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function contactGestionadoClass(gestionado) {
  const g = String(gestionado || "").toLowerCase();
  if (g === "gestionado") return "bo-ver-congreso__estado bo-ver-congreso__estado--ok";
  if (g === "pendiente") return "bo-ver-congreso__estado bo-ver-congreso__estado--pending";
  return "bo-ver-congreso__estado bo-ver-congreso__estado--neutral";
}

function collectEditionPdfs(edicion) {
  const rows = [];
  const push = (label, relPath) => {
    const raw = relPath != null ? String(relPath).trim() : "";
    if (!raw) return;
    const url = mediaUrl(raw);
    rows.push({ label, url });
  };

  const prog = (edicion.programasDescargables || [])[0];
  if (prog) {
    const ap = prog.archivosPorIdioma;
    const hasAp =
      ap &&
      typeof ap === "object" &&
      [ap.es, ap.ca, ap.en].some((x) => x != null && String(x).trim());
    if (hasAp) {
      if (ap.es) push("Programa descargable (castellano)", ap.es);
      if (ap.ca) push("Programa descargable (català)", ap.ca);
      if (ap.en) push("Programa descargable (English)", ap.en);
    } else if (prog.archivo) {
      push("Programa descargable", prog.archivo);
    }
  }

  const intro = edicion.comunicacionesIntro;
  const nc = intro && intro.normativaCard;
  if (nc) {
    const ap = nc.normativaPdfPorIdioma;
    const hasAp =
      ap &&
      typeof ap === "object" &&
      [ap.es, ap.ca, ap.en].some((x) => x != null && String(x).trim());
    if (hasAp) {
      if (ap.es) push("Normativa PDF (castellano)", ap.es);
      if (ap.ca) push("Normativa PDF (català)", ap.ca);
      if (ap.en) push("Normativa PDF (English)", ap.en);
    } else if (nc.normativaPdf) {
      push("Normativa (comunicaciones)", nc.normativaPdf);
    }
  }

  return rows;
}

export default function VerCongresoEdicionPage() {
  const { id } = useParams();
  const history = useHistory();
  const [edicion, setEdicion] = useState(null);
  const [insRows, setInsRows] = useState([]);
  const [insQ, setInsQ] = useState("");
  const [insLoading, setInsLoading] = useState(false);
  const [contactRows, setContactRows] = useState([]);
  const [contactQ, setContactQ] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tallerRows, setTallerRows] = useState([]);
  const [tallerLoading, setTallerLoading] = useState(false);
  const [addTallerOpen, setAddTallerOpen] = useState(null);
  const [addTallerForm, setAddTallerForm] = useState({
    nombre: "",
    email: "",
    dni: "",
  });
  const [addTallerSaving, setAddTallerSaving] = useState(false);
  const [viewTab, setViewTab] = useState(VIEW_TAB_TODO);
  const [tallerSort, setTallerSort] = useState({ key: "createdAt", dir: "desc" });
  const [selectedInsIds, setSelectedInsIds] = useState(() => ({}));
  const [selectedTallerInsIds, setSelectedTallerInsIds] = useState(() => ({}));
  const [bulkDeletingIns, setBulkDeletingIns] = useState(false);
  const [bulkDeletingTaller, setBulkDeletingTaller] = useState(false);
  const languages = useSelector((s) => s.authentication?.languages, shallowEqual);
  const lang =
    (languages &&
      languages[0] &&
      languages[0].isocode &&
      String(languages[0].isocode).trim()) ||
    "es";

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!id) {
      history.push("/congresos");
      return;
    }
    getCongresoEdicionById(id)
      .then((res) => {
        if (res.status === 200) {
          setEdicion(res.data);
        } else {
          alertError({ customMessage: "No se encontró la edición." });
          history.push("/congresos");
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo cargar la edición de congreso.",
        });
        history.push("/congresos");
      })
      .finally(() => disableLoadingData());
  }, [id, history, disableLoadingData]);

  const loadInscripciones = React.useCallback(
    (q) => {
      if (!id) return;
      setInsLoading(true);
      getCongresoInscripcionesByEdicion(id, q || "")
        .then((res) => {
          if (res.status === 200) {
            setInsRows(res.data || []);
            setSelectedInsIds({});
          }
        })
        .catch((error) => {
          alertError({
            error,
            customMessage: "No se pudo cargar el listado de inscripciones.",
          });
        })
        .finally(() => setInsLoading(false));
    },
    [id]
  );

  useEffect(() => {
    if (edicion && id) loadInscripciones("");
  }, [edicion, id, loadInscripciones]);

  const loadContactos = React.useCallback(
    (q) => {
      if (!id) return;
      setContactLoading(true);
      getCongresoContactosByEdicion(id, q || "")
        .then((res) => {
          if (res.status === 200) setContactRows(res.data || []);
        })
        .catch((error) => {
          alertError({
            error,
            customMessage: "No se pudo cargar los contactos de esta edición.",
          });
        })
        .finally(() => setContactLoading(false));
    },
    [id]
  );

  useEffect(() => {
    if (edicion && id) loadContactos("");
  }, [edicion, id, loadContactos]);

  const loadTalleresInscripciones = React.useCallback(() => {
    if (!id) return;
    setTallerLoading(true);
    getCongresoTalleresInscripciones(id)
      .then((res) => {
        if (res.status === 200) {
          setTallerRows(res.data?.talleres || []);
          setSelectedTallerInsIds({});
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo cargar los apuntados a talleres.",
        });
      })
      .finally(() => setTallerLoading(false));
  }, [id]);

  useEffect(() => {
    if (edicion && id) loadTalleresInscripciones();
  }, [edicion, id, loadTalleresInscripciones]);

  const openInscripcionDetalle = (inscripcionId) => {
    if (!id || !inscripcionId) return;
    getCongresoInscripcionById(id, inscripcionId)
      .then((res) => {
        if (res.status === 200) setDetail(res.data);
        else alertError({ customMessage: "No se encontró la inscripción." });
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo cargar el detalle de la inscripción.",
        });
      });
  };

  const openAddTallerDialog = (taller) => {
    setAddTallerForm({ nombre: "", email: "", dni: "" });
    setAddTallerOpen(taller);
  };

  const submitAddTaller = () => {
    if (!id || !addTallerOpen?.tallerId) return;
    const nombre = String(addTallerForm.nombre || "").trim();
    const email = String(addTallerForm.email || "").trim();
    const dni = String(addTallerForm.dni || "").trim();
    if (!nombre) {
      alertError({ customMessage: "El nombre es obligatorio." });
      return;
    }
    if (!dni) {
      alertError({ customMessage: "El DNI/NIE es obligatorio." });
      return;
    }
    setAddTallerSaving(true);
    postCongresoTallerInscripcion(id, {
      tallerId: addTallerOpen.tallerId,
      nombre,
      email,
      dni,
    })
      .then((res) => {
        if (res.status === 201) {
          const over = res.data?.overCapacity;
          alertSuccess({
            customMessage: over
              ? "Persona añadida. Atención: se ha superado el cupo máximo del taller."
              : "Persona añadida al taller.",
          });
          setAddTallerOpen(null);
          loadTalleresInscripciones();
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo añadir a la persona al taller.",
        });
      })
      .finally(() => setAddTallerSaving(false));
  };

  const toggleTallerSort = (key) => {
    setTallerSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === "createdAt" ? "desc" : "asc" };
    });
  };

  const renderTallerSortTh = (col) => {
    const active = tallerSort.key === col.key;
    const arrow = !active ? "↕" : tallerSort.dir === "asc" ? "↑" : "↓";
    return (
      <th key={col.key} className="bo-ver-congreso__sortable-th" scope="col">
        <button
          type="button"
          className={
            "bo-ver-congreso__sort-btn" +
            (active ? " bo-ver-congreso__sort-btn--active" : "")
          }
          onClick={() => toggleTallerSort(col.key)}
          title={
            active
              ? tallerSort.dir === "asc"
                ? "Orden ascendente (clic para descendente)"
                : "Orden descendente (clic para ascendente)"
              : "Ordenar"
          }
        >
          <span>{col.label}</span>
          <span className="bo-ver-congreso__sort-arrow" aria-hidden>
            {arrow}
          </span>
        </button>
      </th>
    );
  };

  const removeTallerInscrito = (taller, row) => {
    if (!id || !row?._id) return;
    const label = row.nombre || row.dni || "esta persona";
    if (
      !window.confirm(
        `¿Eliminar a ${label} del taller «${taller.titulo || taller.tallerId}»?`
      )
    ) {
      return;
    }
    deleteCongresoTallerInscripcion(id, row._id)
      .then((res) => {
        if (res.status === 204 || res.status === 200) {
          alertSuccess({ customMessage: "Persona eliminada del taller." });
          loadTalleresInscripciones();
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudo eliminar a la persona del taller.",
        });
      });
  };

  const toggleInsSelection = (rowId, checked) => {
    const key = String(rowId || "");
    if (!key) return;
    setSelectedInsIds((prev) => {
      const next = { ...prev };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const toggleTallerInsSelection = (rowId, checked) => {
    const key = String(rowId || "");
    if (!key) return;
    setSelectedTallerInsIds((prev) => {
      const next = { ...prev };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const setGroupInsSelection = (rows, checked) => {
    setSelectedInsIds((prev) => {
      const next = { ...prev };
      (rows || []).forEach((r) => {
        const key = String(r?._id || "");
        if (!key) return;
        if (checked) next[key] = true;
        else delete next[key];
      });
      return next;
    });
  };

  const setTallerGroupSelection = (rows, checked) => {
    setSelectedTallerInsIds((prev) => {
      const next = { ...prev };
      (rows || []).forEach((r) => {
        const key = String(r?._id || "");
        if (!key) return;
        if (checked) next[key] = true;
        else delete next[key];
      });
      return next;
    });
  };

  const selectedInsCount = useMemo(
    () => Object.keys(selectedInsIds).length,
    [selectedInsIds]
  );
  const selectedTallerInsCount = useMemo(
    () => Object.keys(selectedTallerInsIds).length,
    [selectedTallerInsIds]
  );

  const removeSelectedInscripciones = () => {
    if (!id || selectedInsCount === 0) return;
    if (
      !window.confirm(
        `¿Eliminar ${selectedInsCount} inscripción${
          selectedInsCount === 1 ? "" : "es"
        } del congreso? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    const ids = Object.keys(selectedInsIds);
    setBulkDeletingIns(true);
    deleteCongresoInscripcionesBulk(id, ids)
      .then((res) => {
        if (res.status === 200) {
          const n = res.data?.deleted ?? ids.length;
          alertSuccess({
            customMessage: `Se eliminaron ${n} inscripción${n === 1 ? "" : "es"}.`,
          });
          loadInscripciones(insQ);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudieron eliminar las inscripciones seleccionadas.",
        });
      })
      .finally(() => setBulkDeletingIns(false));
  };

  const removeSelectedTallerInscripciones = () => {
    if (!id || selectedTallerInsCount === 0) return;
    if (
      !window.confirm(
        `¿Eliminar ${selectedTallerInsCount} persona${
          selectedTallerInsCount === 1 ? "" : "s"
        } de los talleres? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    const ids = Object.keys(selectedTallerInsIds);
    setBulkDeletingTaller(true);
    deleteCongresoTalleresInscripcionesBulk(id, ids)
      .then((res) => {
        if (res.status === 200) {
          const n = res.data?.deleted ?? ids.length;
          alertSuccess({
            customMessage: `Se eliminaron ${n} apuntado${n === 1 ? "" : "s"} a taller.`,
          });
          loadTalleresInscripciones();
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudieron eliminar los apuntados seleccionados.",
        });
      })
      .finally(() => setBulkDeletingTaller(false));
  };

  const contactosBySeccion = useMemo(() => {
    const map = new Map(CONTACTO_SECCIONES.map((s) => [s.origen, []]));
    (contactRows || []).forEach((row) => {
      const key = row.origenFormulario;
      if (map.has(key)) map.get(key).push(row);
    });
    return CONTACTO_SECCIONES.map((sec) => ({
      ...sec,
      rows: map.get(sec.origen) || [],
    }));
  }, [contactRows]);

  const countByContactoKey = useMemo(() => {
    const map = {
      envio: { total: 0, pendientes: 0 },
      patrocinio: { total: 0, pendientes: 0 },
      contacto: { total: 0, pendientes: 0 },
    };
    (contactosBySeccion || []).forEach((sec) => {
      const rows = sec.rows || [];
      map[sec.key] = {
        total: rows.length,
        pendientes: rows.filter(isContactoPendiente).length,
      };
    });
    return map;
  }, [contactosBySeccion]);

  const tallerInscritosFlat = useMemo(() => {
    const out = [];
    (tallerRows || []).forEach((tal) => {
      (tal.inscritos || []).forEach((row) => out.push(row));
    });
    return out;
  }, [tallerRows]);

  /** Timestamp de última visita “cerrada”; al entrar en un apartado se congela para el badge. */
  const [visitBaseline, setVisitBaseline] = useState({
    talleres: null,
    inscripciones: null,
  });

  useEffect(() => {
    if (!id) return undefined;
    if (viewTab === VIEW_TAB_TALLERES) {
      setVisitBaseline((prev) => ({
        ...prev,
        talleres: readVerCongresoLastVisit(id, VIEW_TAB_TALLERES),
      }));
      return () => {
        writeVerCongresoLastVisit(id, VIEW_TAB_TALLERES);
      };
    }
    if (viewTab === VIEW_TAB_INSCRIPCIONES) {
      setVisitBaseline((prev) => ({
        ...prev,
        inscripciones: readVerCongresoLastVisit(id, VIEW_TAB_INSCRIPCIONES),
      }));
      return () => {
        writeVerCongresoLastVisit(id, VIEW_TAB_INSCRIPCIONES);
      };
    }
    return undefined;
  }, [viewTab, id]);

  const nuevosTalleres = useMemo(() => {
    const since =
      viewTab === VIEW_TAB_TALLERES
        ? visitBaseline.talleres
        : readVerCongresoLastVisit(id, VIEW_TAB_TALLERES);
    return countCreatedSince(tallerInscritosFlat, since);
  }, [tallerInscritosFlat, id, viewTab, visitBaseline.talleres]);

  const nuevosInscripciones = useMemo(() => {
    const since =
      viewTab === VIEW_TAB_INSCRIPCIONES
        ? visitBaseline.inscripciones
        : readVerCongresoLastVisit(id, VIEW_TAB_INSCRIPCIONES);
    const finalized = (insRows || []).filter((r) => r.estado === "completado");
    return countCreatedSince(finalized, since);
  }, [insRows, id, viewTab, visitBaseline.inscripciones]);

  const talleresPlazasResumen = useMemo(() => {
    let ocupadas = 0;
    let totales = 0;
    let hasCapacidad = false;
    (tallerRows || []).forEach((tal) => {
      if (tal.orphan) return;
      ocupadas += Number(tal.ocupados) || 0;
      if (tal.capacidad != null && Number.isFinite(Number(tal.capacidad))) {
        hasCapacidad = true;
        totales += Math.max(0, Math.floor(Number(tal.capacidad)));
      }
    });
    return { ocupadas, totales, hasCapacidad };
  }, [tallerRows]);

  const showSection = (tabId) => viewTab === VIEW_TAB_TODO || viewTab === tabId;

  const contactosVisibles = useMemo(() => {
    if (viewTab === VIEW_TAB_TODO) return contactosBySeccion;
    if (viewTab === VIEW_TAB_ENVIOS) {
      return contactosBySeccion.filter((s) => s.key === "envio");
    }
    if (viewTab === VIEW_TAB_PATROCINIOS) {
      return contactosBySeccion.filter((s) => s.key === "patrocinio");
    }
    if (viewTab === VIEW_TAB_CONTACTO) {
      return contactosBySeccion.filter((s) => s.key === "contacto");
    }
    return [];
  }, [contactosBySeccion, viewTab]);

  const showContactosBoard =
    viewTab === VIEW_TAB_TODO ||
    viewTab === VIEW_TAB_ENVIOS ||
    viewTab === VIEW_TAB_PATROCINIOS ||
    viewTab === VIEW_TAB_CONTACTO;

  const { finalizedGroups, otherGroups, totalInscripciones } = useMemo(() => {
    const rows = insRows || [];
    const finalized = rows.filter((r) => r.estado === "completado");
    const other = rows.filter((r) => r.estado !== "completado");
    return {
      finalizedGroups: groupInscripcionesByPlan(finalized),
      otherGroups: groupInscripcionesByPlan(other),
      /** Contador del badge / resumen: solo pago completado. */
      totalInscripciones: finalized.length,
    };
  }, [insRows]);

  const canExportAll = useMemo(
    () =>
      finalizedGroups.length > 0 ||
      otherGroups.length > 0 ||
      contactosBySeccion.some((sec) => sec.rows.length > 0) ||
      tallerInscritosFlat.length > 0,
    [finalizedGroups, otherGroups, contactosBySeccion, tallerInscritosFlat]
  );

  const renderContactoTable = (sec) => {
    if (sec.key === "envio") {
      return (
        <div className="bo-ver-congreso__insc-table-wrap">
          <table className="bo-ver-congreso__insc-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>DNI</th>
                <th>PDF</th>
                <th>Estado</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {sec.rows.map((r) => {
                const pdfUrl = resolveDocumentoPdfUrl(r.documentoPdf);
                return (
                  <tr key={r._id}>
                    <td>{formatDetailDate(r.createdAt)}</td>
                    <td>{r.name || "—"}</td>
                    <td>{r.email || "—"}</td>
                    <td>{r.dni || "—"}</td>
                    <td>
                      {pdfUrl ? (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bo-ver-congreso__contact-pdf-link"
                        >
                          Descargar PDF
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className={contactGestionadoClass(r.gestionado)}>
                        {r.gestionado || "—"}
                      </span>
                    </td>
                    <td className="bo-ver-congreso__insc-actions">
                      <button
                        type="button"
                        className="bo-ver-congreso__insc-btn"
                        onClick={() => openContactFicha(r._id)}
                      >
                        Ver ficha
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="bo-ver-congreso__insc-table-wrap">
        <table className="bo-ver-congreso__insc-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Asunto</th>
              <th>Mensaje</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {sec.rows.map((r) => (
              <tr key={r._id}>
                <td>{formatDetailDate(r.createdAt)}</td>
                <td>{r.name || "—"}</td>
                <td>{r.email || "—"}</td>
                <td>{r.tel || "—"}</td>
                <td>{truncateText(r.asunto, 48)}</td>
                <td>{truncateText(r.mensaje, 64)}</td>
                <td>
                  <span className={contactGestionadoClass(r.gestionado)}>{r.gestionado || "—"}</span>
                </td>
                <td className="bo-ver-congreso__insc-actions">
                  <button
                    type="button"
                    className="bo-ver-congreso__insc-btn"
                    onClick={() => openContactFicha(r._id)}
                  >
                    Ver ficha
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPlanGroupTables = (groups) =>
    groups.map((g) => {
      const groupIds = (g.rows || []).map((r) => String(r._id));
      const selectedInGroup = groupIds.filter((rid) => selectedInsIds[rid]).length;
      const allSelected = groupIds.length > 0 && selectedInGroup === groupIds.length;
      const someSelected = selectedInGroup > 0 && !allSelected;
      return (
        <div key={g.planId} className="bo-ver-congreso__insc-group">
          <div className="bo-ver-congreso__insc-plan-head">
            <span className="bo-ver-congreso__insc-plan-name">{g.label}</span>
            <span className="bo-ver-congreso__insc-plan-meta">
              Desde {formatInscripcionEur(g.maxPrice)}
            </span>
          </div>
          <div className="bo-ver-congreso__insc-table-wrap">
            <table className="bo-ver-congreso__insc-table">
              <thead>
                <tr>
                  <th className="bo-ver-congreso__insc-check-col">
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => setGroupInsSelection(g.rows, e.target.checked)}
                      inputProps={{ "aria-label": "Seleccionar todas del plan" }}
                    />
                  </th>
                  <th>Estado</th>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Importe</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => {
                  const rid = String(r._id);
                  return (
                    <tr key={r._id} className={selectedInsIds[rid] ? "bo-ver-congreso__insc-row--selected" : undefined}>
                      <td className="bo-ver-congreso__insc-check-col">
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={!!selectedInsIds[rid]}
                          onChange={(e) => toggleInsSelection(rid, e.target.checked)}
                          inputProps={{ "aria-label": "Seleccionar inscripción" }}
                        />
                      </td>
                      <td>
                        <span className={inscripcionEstadoPillClass(r.estado)} title={r.estado || ""}>
                          {inscripcionEstadoLabel(r.estado)}
                        </span>
                      </td>
                      <td>{r.emailContacto || r.formData?.email || "—"}</td>
                      <td>
                        {[r.formData?.nombre, r.formData?.apellido1, r.formData?.apellido2]
                          .filter(Boolean)
                          .join(" ")
                          .trim() || "—"}
                      </td>
                      <td>{formatInscripcionEur(r.precioImporte)}</td>
                      <td className="bo-ver-congreso__insc-actions">
                        <button
                          type="button"
                          className="bo-ver-congreso__insc-btn"
                          onClick={() => setDetail(r)}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    });

  const themeStyle = useMemo(
    () => (edicion ? buildEditionThemeStyle(edicion) : {}),
    [edicion]
  );

  const pdfRows = useMemo(() => (edicion ? collectEditionPdfs(edicion) : []), [edicion]);

  const viewApartadoMeta = useMemo(() => {
    const tall = talleresPlazasResumen.hasCapacidad
      ? `${talleresPlazasResumen.ocupadas}/${talleresPlazasResumen.totales}`
      : String(talleresPlazasResumen.ocupadas);
    const fmtPend = (key) => {
      const c = countByContactoKey[key] || { pendientes: 0, total: 0 };
      return `${c.pendientes}/${c.total}`;
    };
    return {
      [VIEW_TAB_TALLERES]: { text: tall, nuevos: nuevosTalleres },
      [VIEW_TAB_INSCRIPCIONES]: {
        text: String(totalInscripciones),
        nuevos: nuevosInscripciones,
      },
      [VIEW_TAB_PDFS]: { text: String(pdfRows.length), nuevos: 0 },
      [VIEW_TAB_ENVIOS]: { text: fmtPend("envio"), nuevos: 0 },
      [VIEW_TAB_PATROCINIOS]: { text: fmtPend("patrocinio"), nuevos: 0 },
      [VIEW_TAB_CONTACTO]: { text: fmtPend("contacto"), nuevos: 0 },
    };
  }, [
    talleresPlazasResumen,
    totalInscripciones,
    pdfRows.length,
    countByContactoKey,
    nuevosTalleres,
    nuevosInscripciones,
  ]);

  const openContactFicha = (contactId) => {
    history.push({
      pathname: `/edit-contact/${contactId}`,
      state: { returnTo: `/ver-congreso-edicion/${id}` },
    });
  };

  if (isLoadingData || !edicion) return <ContentSkeleton />;

  return (
    <Card className="bo-ver-congreso-page">
      <CardHeader
        title={
          <div className="bo-ver-congreso-page__header-wrap">
            <span className="bo-ver-congreso-page__header-kicker">Vista previa administrativa</span>
            <h3 className="card-label bo-ver-congreso-page__header-heading">
              <span className="bo-ver-congreso-page__header-heading-text">Gestión de la edición</span>
              <span className="bo-ver-congreso-page__header-year">{edicion.year || "—"}</span>
            </h3>
          </div>
        }
        toolbar={
          <CardHeaderToolbar>
            <Button
              className="mr-3"
              variant="outlined"
              startIcon={<GetAppIcon />}
              disabled={!canExportAll}
              onClick={() => {
                const ok = exportCongresoEdicionTodoExcel({
                  year: edicion.year,
                  finalizedGroups,
                  otherGroups,
                  contactosBySeccion,
                  tallerRows,
                });
                if (ok) {
                  alertSuccess({
                    customMessage:
                      "Se ha descargado el Excel con todas las hojas de esta edición (inscripciones, talleres y formularios).",
                  });
                }
              }}
            >
              Exportar Todo
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/congresos")}
              style={{ backgroundColor: "#f3691a", color: "#fff" }}
            >
              Volver al listado
            </Button>
          </CardHeaderToolbar>
        }
      />
      <CardBody>
        <div className="bo-ver-congreso">
          <div className="bo-ver-congreso__intro">
            <Typography variant="body2" color="textSecondary" className="mb-2">
              Edición {edicion.year || "—"}
              {edicion.published ? " · Publicada en la web de congresos" : " · Borrador"}
            </Typography>
            <Typography variant="h5" component="h2" className="mb-0">
              {getLocalizedText(edicion.titulo, lang) || `Congreso ${edicion.year || ""}`}
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mt-3 mb-0">
              Vista previa <strong>solo para backoffice</strong>. Usa las cajas para filtrar: talleres,
              inscripciones, PDFs y formularios recibidos. Para ver la web pública usa el botón de preview
              desde el listado de congresos.
            </Typography>
          </div>

          <div
            className="congreso-edicion-apartados bo-ver-congreso__apartados"
            role="tablist"
            aria-label="Apartados de gestión"
          >
            {VIEW_APARTADOS.map(({ id, label, Icon }) => {
              const meta = viewApartadoMeta[id];
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={viewTab === id}
                  className={
                    "congreso-edicion-apartado" +
                    (viewTab === id ? " congreso-edicion-apartado--active" : "")
                  }
                  onClick={() => setViewTab(id)}
                >
                  <Icon className="congreso-edicion-apartado__icon" />
                  <span className="congreso-edicion-apartado__label">{label}</span>
                  {meta ? (
                    <span className="congreso-edicion-apartado__meta-wrap">
                      <span className="congreso-edicion-apartado__meta">{meta.text}</span>
                      {meta.nuevos > 0 ? (
                        <span className="congreso-edicion-apartado__nuevos">
                          +{meta.nuevos} nuevo{meta.nuevos === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {showSection(VIEW_TAB_PDFS) ? (
          <section className="bo-ver-congreso__pdfs">
              <h2 className="bo-ver-congreso__pdfs-title">PDFs de esta edición</h2>
              {pdfRows.length === 0 ? (
                <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                  No hay PDFs configurados (programa descargable ni normativa en comunicaciones).
                </p>
              ) : (
                <ul className="bo-ver-congreso__pdf-list">
                  {pdfRows.map((row, idx) => (
                    <li key={`${idx}-${row.url}`} className="bo-ver-congreso__pdf-row">
                      <span className="bo-ver-congreso__pdf-label">{row.label}</span>
                      <div className="bo-ver-congreso__pdf-actions">
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Abrir / descargar
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {showContactosBoard ? (
            <section className="bo-ver-congreso__contacts">
              <div className="bo-ver-congreso__insc-board">
                <h2 className="bo-ver-congreso__insc-title">
                  {viewTab === VIEW_TAB_ENVIOS
                    ? "Envíos de trabajo"
                    : viewTab === VIEW_TAB_PATROCINIOS
                      ? "Patrocinios"
                      : viewTab === VIEW_TAB_CONTACTO
                        ? "Contacto congreso"
                        : "Formularios de esta edición"}
                </h2>
                <p className="bo-ver-congreso__insc-lead">
                  {viewTab === VIEW_TAB_TODO
                    ? "Contactos recibidos desde la web de congresos vinculados a esta edición: patrocinio, envío de trabajos y contacto general."
                    : "Mensajes recibidos desde la web de congresos para este apartado."}
                </p>
                <div className="bo-ver-congreso__insc-toolbar">
                  <div className="bo-ver-congreso__insc-toolbar-field">
                    <TextField
                      className="bo-ver-congreso__insc-textfield"
                      size="medium"
                      label="Buscar contactos (nombre, email, DNI, asunto)"
                      variant="outlined"
                      value={contactQ}
                      onChange={(e) => setContactQ(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                  <Button
                    className="bo-ver-congreso__insc-cta"
                    variant="contained"
                    disableElevation
                    disabled={contactLoading}
                    onClick={() => loadContactos(contactQ)}
                  >
                    Buscar
                  </Button>
                </div>
                {contactLoading ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">Cargando contactos…</p>
                ) : contactosVisibles.every((sec) => sec.rows.length === 0) ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">
                    No hay envíos de formularios para este apartado.
                  </p>
                ) : (
                  contactosVisibles.map((sec) => {
                    const SecIcon = sec.Icon;
                    return (
                    <div key={sec.key} className="bo-ver-congreso__insc-macro">
                      <div className="bo-ver-congreso__insc-panel">
                        <div className="bo-ver-congreso__insc-section-head">
                          <div className="bo-ver-congreso__insc-section-title-block">
                            <div className="bo-ver-congreso__insc-icon-ring" aria-hidden>
                              <SecIcon size={22} />
                            </div>
                            <h3 className="bo-ver-congreso__insc-section-title">
                              {sec.title}
                              <span className="bo-ver-congreso__contact-count"> ({sec.rows.length})</span>
                            </h3>
                          </div>
                          <Button
                            className="bo-ver-congreso__insc-export-btn"
                            variant="outlined"
                            size="small"
                            startIcon={<GetAppIcon />}
                            disabled={contactLoading || sec.rows.length === 0}
                            onClick={() => {
                              const ok = exportCongresoContactosExcel({
                                year: edicion.year,
                                sectionKey: sec.key,
                                title: sec.title,
                                rows: sec.rows,
                              });
                              if (ok) {
                                alertSuccess({
                                  customMessage: `Se ha descargado el Excel de ${sec.title.toLowerCase()}.`,
                                });
                              }
                            }}
                          >
                            Exportar Excel
                          </Button>
                        </div>
                        <hr className="bo-ver-congreso__insc-rule" />
                        {sec.rows.length === 0 ? (
                          <p className="bo-ver-congreso__insc-panel-empty">
                            Ningún envío en esta categoría.
                          </p>
                        ) : (
                          renderContactoTable(sec)
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </section>
          ) : null}

          {showSection(VIEW_TAB_TALLERES) ? (
            <section className="bo-ver-congreso__insc bo-ver-congreso__talleres">
              <div className="bo-ver-congreso__insc-board">
                <div className="bo-ver-congreso__insc-section-head">
                  <div className="bo-ver-congreso__insc-section-title-block">
                    <h2 className="bo-ver-congreso__insc-title mb-0">
                      <BsPeople className="bo-ver-congreso__talleres-title-icon" aria-hidden />
                      Talleres
                    </h2>
                  </div>
                  <Button
                    className="bo-ver-congreso__insc-export-btn"
                    variant="outlined"
                    size="small"
                    startIcon={<GetAppIcon />}
                    disabled={tallerLoading || tallerInscritosFlat.length === 0}
                    onClick={() => {
                      const ok = exportCongresoTalleresInscripcionesExcel({
                        year: edicion.year,
                        tallerRows,
                      });
                      if (ok) {
                        alertSuccess({
                          customMessage:
                            "Se ha descargado el Excel con los apuntados a talleres, agrupados por taller.",
                        });
                      }
                    }}
                  >
                    Exportar Excel
                  </Button>
                </div>
                <p className="bo-ver-congreso__insc-lead">
                  Gestiona quiénes están apuntados a cada taller. Puedes superar el cupo máximo; en ese caso se
                  muestra un aviso en rojo. Si el DNI coincide con una inscripción al congreso, puedes abrir su
                  ficha.
                </p>
                {selectedTallerInsCount > 0 ? (
                  <div className="bo-ver-congreso__insc-bulk-bar">
                    <span>
                      {selectedTallerInsCount} seleccionado
                      {selectedTallerInsCount === 1 ? "" : "s"}
                    </span>
                    <Button
                      className="bo-ver-congreso__insc-cta bo-ver-congreso__insc-cta--danger"
                      variant="contained"
                      disableElevation
                      size="small"
                      disabled={bulkDeletingTaller}
                      onClick={removeSelectedTallerInscripciones}
                    >
                      {bulkDeletingTaller ? "Eliminando…" : "Eliminar seleccionados"}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      disabled={bulkDeletingTaller}
                      onClick={() => setSelectedTallerInsIds({})}
                    >
                      Limpiar selección
                    </Button>
                  </div>
                ) : null}
                {tallerLoading ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">Cargando talleres…</p>
                ) : tallerRows.length === 0 ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">
                    No hay talleres definidos en esta edición. Configúralos en la edición (pestaña Talleres).
                  </p>
                ) : (
                  tallerRows.map((tal) => {
                    const ocupados = Number(tal.ocupados) || 0;
                    const capacidad =
                      tal.capacidad != null && Number.isFinite(Number(tal.capacidad))
                        ? Math.max(0, Math.floor(Number(tal.capacidad)))
                        : null;
                    const libres =
                      capacidad != null ? Math.max(0, capacidad - ocupados) : null;
                    const isFull = capacidad != null && libres <= 0;
                    const over = !!tal.overCapacity || (capacidad != null && ocupados > capacidad);
                    const cupoLabel =
                      capacidad == null
                        ? `${ocupados} apuntados · sin límite`
                        : isFull
                          ? `Completo · ${ocupados} / ${capacidad}`
                          : `${ocupados} / ${capacidad}`;
                    return (
                      <div
                        key={tal.tallerId}
                        className={
                          "bo-ver-congreso__taller-card" +
                          (isFull || over ? " bo-ver-congreso__taller-card--over" : "")
                        }
                      >
                        <div className="bo-ver-congreso__insc-section-head">
                          <div className="bo-ver-congreso__insc-section-title-block">
                            <h3 className="bo-ver-congreso__insc-section-title">
                              {tal.titulo || "Taller"}
                              {tal.orphan ? " (eliminado del programa)" : ""}
                            </h3>
                            <p className="bo-ver-congreso__taller-meta">
                              {[tal.diaEtiqueta, tal.hora, tal.sala].filter(Boolean).join(" · ") ||
                                "Sin día/hora"}
                            </p>
                          </div>
                          <div className="bo-ver-congreso__taller-head-actions">
                            <span
                              className={
                                "bo-ver-congreso__taller-cupo" +
                                (isFull || over ? " bo-ver-congreso__taller-cupo--over" : "")
                              }
                            >
                              {cupoLabel}
                              {ocupados > (capacidad ?? Infinity) ? " · supera cupo" : ""}
                            </span>
                            <Button
                              className="bo-ver-congreso__insc-export-btn"
                              variant="outlined"
                              size="small"
                              startIcon={<GetAppIcon />}
                              disabled={(tal.inscritos || []).length === 0}
                              onClick={() => {
                                const ok = exportCongresoTallerInscripcionesExcel({
                                  year: edicion.year,
                                  taller: tal,
                                });
                                if (ok) {
                                  alertSuccess({
                                    customMessage: `Se ha descargado el Excel del taller «${
                                      tal.titulo || "Taller"
                                    }».`,
                                  });
                                }
                              }}
                            >
                              Exportar Excel
                            </Button>
                            {!tal.orphan ? (
                              <Button
                                className="bo-ver-congreso__insc-cta"
                                variant="contained"
                                disableElevation
                                size="small"
                                onClick={() => openAddTallerDialog(tal)}
                              >
                                Añadir
                              </Button>
                            ) : null}
                          </div>
                        </div>
                        {(tal.inscritos || []).length === 0 ? (
                          <p className="bo-ver-congreso__insc-panel-empty">Nadie apuntado todavía.</p>
                        ) : (
                          <div className="bo-ver-congreso__insc-table-wrap">
                            <table className="bo-ver-congreso__insc-table">
                              <thead>
                                <tr>
                                  <th className="bo-ver-congreso__insc-check-col">
                                    {(() => {
                                      const rows = tal.inscritos || [];
                                      const ids = rows.map((r) => String(r._id));
                                      const selectedInGroup = ids.filter(
                                        (rid) => selectedTallerInsIds[rid]
                                      ).length;
                                      const allSelected =
                                        ids.length > 0 && selectedInGroup === ids.length;
                                      const someSelected =
                                        selectedInGroup > 0 && !allSelected;
                                      return (
                                        <Checkbox
                                          color="primary"
                                          size="small"
                                          checked={allSelected}
                                          indeterminate={someSelected}
                                          onChange={(e) =>
                                            setTallerGroupSelection(rows, e.target.checked)
                                          }
                                          inputProps={{
                                            "aria-label": "Seleccionar todos del taller",
                                          }}
                                        />
                                      );
                                    })()}
                                  </th>
                                  {TALLER_SORT_COLUMNS.map(renderTallerSortTh)}
                                  <th aria-label="Acciones" />
                                </tr>
                              </thead>
                              <tbody>
                                {sortTallerInscritos(tal.inscritos, tallerSort).map((row) => {
                                  const rid = String(row._id);
                                  return (
                                  <tr
                                    key={row._id}
                                    className={
                                      [
                                        isFull || over
                                          ? "bo-ver-congreso__taller-row--over"
                                          : "",
                                        selectedTallerInsIds[rid]
                                          ? "bo-ver-congreso__insc-row--selected"
                                          : "",
                                      ]
                                        .filter(Boolean)
                                        .join(" ") || undefined
                                    }
                                  >
                                    <td className="bo-ver-congreso__insc-check-col">
                                      <Checkbox
                                        color="primary"
                                        size="small"
                                        checked={!!selectedTallerInsIds[rid]}
                                        onChange={(e) =>
                                          toggleTallerInsSelection(rid, e.target.checked)
                                        }
                                        inputProps={{
                                          "aria-label": "Seleccionar apuntado",
                                        }}
                                      />
                                    </td>
                                    <td>{row.nombre || "—"}</td>
                                    <td>{row.email || "—"}</td>
                                    <td>{row.dni || "—"}</td>
                                    <td>{formatDetailDate(row.createdAt)}</td>
                                    <td>
                                      {row.inscripcionCongresoId ? (
                                        <button
                                          type="button"
                                          className="bo-ver-congreso__insc-btn"
                                          onClick={() =>
                                            openInscripcionDetalle(row.inscripcionCongresoId)
                                          }
                                        >
                                          Inscrito al congreso
                                          {row.inscripcionCongreso?.estado
                                            ? ` (${inscripcionEstadoLabel(
                                                row.inscripcionCongreso.estado
                                              )})`
                                            : ""}
                                        </button>
                                      ) : (
                                        <span className="bo-ver-congreso__taller-no-match">
                                          Sin inscripción
                                        </span>
                                      )}
                                    </td>
                                    <td className="bo-ver-congreso__insc-actions">
                                      <button
                                        type="button"
                                        className="bo-ver-congreso__insc-btn bo-ver-congreso__insc-btn--danger"
                                        onClick={() => removeTallerInscrito(tal, row)}
                                      >
                                        Eliminar
                                      </button>
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          ) : null}

          {showSection(VIEW_TAB_INSCRIPCIONES) ? (
            <section className="bo-ver-congreso__insc">
              <div className="bo-ver-congreso__insc-board">
                <h2 className="bo-ver-congreso__insc-title">Inscripciones</h2>
                <p className="bo-ver-congreso__insc-lead">
                  Listado de la edición: primero las inscripciones con pago completado; al final, pendientes de
                  pago, pagos fallidos o canceladas. Agrupadas por plan.
                </p>
                <div className="bo-ver-congreso__insc-toolbar">
                  <div className="bo-ver-congreso__insc-toolbar-field">
                    <TextField
                      className="bo-ver-congreso__insc-textfield"
                      size="medium"
                      label="Buscar (nombre, email, DNI)"
                      variant="outlined"
                      value={insQ}
                      onChange={(e) => setInsQ(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                  <Button
                    className="bo-ver-congreso__insc-cta"
                    variant="contained"
                    disableElevation
                    disabled={insLoading}
                    onClick={() => loadInscripciones(insQ)}
                  >
                    Buscar
                  </Button>
                  {selectedInsCount > 0 ? (
                    <>
                      <Button
                        className="bo-ver-congreso__insc-cta bo-ver-congreso__insc-cta--danger"
                        variant="contained"
                        disableElevation
                        disabled={bulkDeletingIns}
                        onClick={removeSelectedInscripciones}
                      >
                        {bulkDeletingIns
                          ? "Eliminando…"
                          : `Eliminar (${selectedInsCount})`}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        disabled={bulkDeletingIns}
                        onClick={() => setSelectedInsIds({})}
                      >
                        Limpiar
                      </Button>
                    </>
                  ) : null}
                </div>
                {insLoading ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">Cargando inscripciones…</p>
                ) : totalInscripciones === 0 && otherGroups.length === 0 ? (
                  <p className="bo-ver-congreso__insc-lead mb-0">No hay inscripciones registradas.</p>
                ) : (
                  <>
                    <div className="bo-ver-congreso__insc-macro">
                      <div className="bo-ver-congreso__insc-panel">
                        <div className="bo-ver-congreso__insc-section-head">
                          <div className="bo-ver-congreso__insc-section-title-block">
                            <div className="bo-ver-congreso__insc-icon-ring" aria-hidden>
                              <BsCheck2Circle size={22} />
                            </div>
                            <h3 className="bo-ver-congreso__insc-section-title">
                              Finalizadas (pago completado)
                            </h3>
                          </div>
                          <Button
                            className="bo-ver-congreso__insc-export-btn"
                            variant="outlined"
                            size="small"
                            startIcon={<GetAppIcon />}
                            disabled={insLoading || finalizedGroups.length === 0}
                            onClick={() => {
                              const ok = exportCongresoInscripcionesExcel({
                                year: edicion.year,
                                finalizedGroups,
                              });
                              if (ok) {
                                alertSuccess({
                                  customMessage:
                                    "Se ha descargado el Excel con los inscritos (pago completado), agrupados por plan.",
                                });
                              }
                            }}
                          >
                            Exportar Excel
                          </Button>
                        </div>
                        <hr className="bo-ver-congreso__insc-rule" />
                        {finalizedGroups.length === 0 ? (
                          <p className="bo-ver-congreso__insc-panel-empty">
                            Ninguna inscripción con pago finalizado en este criterio de búsqueda.
                          </p>
                        ) : (
                          renderPlanGroupTables(finalizedGroups)
                        )}
                      </div>
                    </div>
                    <div className="bo-ver-congreso__insc-macro">
                      <div className="bo-ver-congreso__insc-panel bo-ver-congreso__insc-panel--pending">
                        <div className="bo-ver-congreso__insc-section-head">
                          <div className="bo-ver-congreso__insc-icon-ring" aria-hidden>
                            <BsClockHistory size={22} />
                          </div>
                          <h3 className="bo-ver-congreso__insc-section-title">
                            Pendientes de pago, pago fallido o canceladas
                          </h3>
                        </div>
                        <hr className="bo-ver-congreso__insc-rule" />
                        {otherGroups.length === 0 ? (
                          <p className="bo-ver-congreso__insc-panel-empty">
                            No hay inscripciones abiertas o rechazadas.
                          </p>
                        ) : (
                          renderPlanGroupTables(otherGroups)
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </CardBody>
      <Dialog
        open={!!addTallerOpen}
        onClose={() => !addTallerSaving && setAddTallerOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: "bo-ver-congreso-insc-dialog", style: themeStyle }}
      >
        <DialogTitle disableTypography>
          <div className="bo-ver-congreso-insc-dialog__title-wrap">
            <span className="bo-ver-congreso-insc-dialog__title-kicker">Añadir al taller</span>
            <h3 className="card-label bo-ver-congreso-insc-dialog__title-main">
              {addTallerOpen?.titulo || "Taller"}
            </h3>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre"
            variant="outlined"
            fullWidth
            margin="normal"
            value={addTallerForm.nombre}
            onChange={(e) =>
              setAddTallerForm((f) => ({ ...f, nombre: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={addTallerForm.email}
            onChange={(e) =>
              setAddTallerForm((f) => ({ ...f, email: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="DNI / NIE"
            variant="outlined"
            fullWidth
            margin="normal"
            value={addTallerForm.dni}
            onChange={(e) =>
              setAddTallerForm((f) => ({ ...f, dni: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            helperText="Si coincide con una inscripción al congreso, podrás ver su ficha."
          />
          {addTallerOpen?.capacidad != null &&
          (addTallerOpen.ocupados || 0) >= addTallerOpen.capacidad ? (
            <Typography
              color="error"
              variant="body2"
              className="bo-ver-congreso__taller-warn"
            >
              Este taller ya está al cupo o lo supera ({addTallerOpen.ocupados || 0} /{" "}
              {addTallerOpen.capacidad}). Puedes añadir igual; se marcará en rojo.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddTallerOpen(null)}
            disabled={addTallerSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={submitAddTaller}
            variant="contained"
            color="primary"
            disableElevation
            disabled={addTallerSaving}
            className="bo-ver-congreso-insc-dialog__close"
          >
            {addTallerSaving ? "Guardando…" : "Añadir"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="lg"
        fullWidth
        scroll="body"
        PaperProps={{ className: "bo-ver-congreso-insc-dialog", style: themeStyle }}
      >
        <DialogTitle className="bo-ver-congreso-insc-dialog__title" disableTypography>
          <div className="bo-ver-congreso-insc-dialog__title-wrap">
            <span className="bo-ver-congreso-insc-dialog__title-kicker">Detalle de inscripción</span>
            <h3 className="card-label bo-ver-congreso-insc-dialog__title-main">
              {detail ? participantDisplayName(detail) : "Inscripción"}
            </h3>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {detail ? <InscripcionDetailContent detail={detail} /> : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetail(null)}
            variant="contained"
            disableElevation
            className="bo-ver-congreso-insc-dialog__close"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
